import React, { useEffect, useState } from "react";
import { Download, Share } from "lucide-react";
import { toast } from "sonner";
import {
  hasDeferredPrompt,
  isIOSSafari,
  isStandalone,
  onInstallStateChange,
  triggerNativeInstall,
} from "@/lib/install";

/**
 * A discreet inline CTA for the admin surface — shows an install button when
 * the browser deems /admin installable, or an iOS “Share → Add to Home”
 * hint on Safari. Hides itself when already running as an installed PWA.
 */
const AdminInstallChip = () => {
  const [tick, setTick] = useState(0);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => onInstallStateChange(() => setTick((n) => n + 1)), []);

  if (isStandalone()) return null;

  const iOS = isIOSSafari();
  const canPrompt = hasDeferredPrompt();

  if (!iOS && !canPrompt) return null; // nothing to offer right now

  const onClick = async () => {
    if (iOS) {
      setShowIos(true);
      return;
    }
    const res = await triggerNativeInstall();
    if (res.ok) toast.success("Admin app installed");
    else if (res.outcome === "dismissed") toast("You can install any time");
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-accent/40 text-accent hover:border-accent hover:bg-accent/10 transition-colors text-[11px] tracking-[0.16em] uppercase"
        data-testid="admin-install-chip"
        title="Install the admin app to your home screen"
      >
        <Download className="w-3.5 h-3.5" />
        Install admin app
      </button>

      {showIos && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-foreground/45 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          data-testid="admin-install-ios"
          onClick={() => setShowIos(false)}
        >
          <div
            className="w-full max-w-sm bg-background border border-border rounded-2xl shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="editorial-kicker text-foreground/50">Install admin app</p>
            <h3 className="font-serif-display text-2xl mt-1">Tap Share → Add to Home Screen</h3>
            <div className="mt-4 flex items-center gap-3 rounded-lg border border-border p-3">
              <div className="w-10 h-10 rounded-md bg-foreground/5 flex items-center justify-center">
                <Share className="w-5 h-5" />
              </div>
              <p className="text-sm text-foreground/70">Look for the Share icon at the bottom of Safari, then choose “Add to Home Screen”.</p>
            </div>
            <div className="mt-5 text-right">
              <button
                type="button"
                onClick={() => setShowIos(false)}
                className="h-10 px-4 rounded-lg bg-foreground text-background text-[12px] tracking-[0.14em] uppercase hover:bg-foreground/90"
                data-testid="admin-install-ios-close"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminInstallChip;
